import { useSelector } from "react-redux";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  downloadChargeFilePDF,
  getChargesForClientsBudgetHeading,
  selectCharges,
  selectLoadingCharges,
} from "./ClientBudgetHeaderChargesTableSlice";
import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams } from "react-router-dom";
import { faDiagramCells, faDownload } from "@fortawesome/pro-solid-svg-icons";
import { useEffect } from "react";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { ClientPortalBudgetChargeModel } from "services/api/cplapi/CPLAPI";
import { downloadFile } from "cpl-react-common/Helpers/FileHelpers";

const ClientTransactionsTable: React.FC = () => {
  const { clientID, budgetID, budgetHeaderID } = useParams();
  const thunkDispatch = useThunkDispatch();
  const charges = useSelector(selectCharges);
  const isLoading = useSelector(selectLoadingCharges);

  const columnHelper = createColumnHelper<ClientPortalBudgetChargeModel>();
  const columnDefinition = [
    columnHelper.display({
      id: "icon",
      cell: () => (
        <div className="d-flex">
          <FontAwesomeIcon icon={faDiagramCells} size="lg" className="cpl-brand-primary" />
        </div>
      ),
      enableHiding: false,
    }),
    columnHelper.accessor((row) => row.transactionDate, {
      id: "Transaction Date",
      cell: (info) => info.getValue()?.toCPLUserFriendlyDateString(),
      header: () => <>Transaction Date</>,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.description, {
      id: "Description",
      cell: (info) => info.getValue(),
      header: () => <>Description</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.totalAmount, {
      id: "Full Amount",
      cell: (info) => info.getValue()?.toGBPString(),
      header: () => <>Full Amount</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.accessor((row) => row.shareDivide, {
      id: "Your Share",
      cell: (info) => info.getValue(),
      header: () => <>Your Share</>,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.shareAmount, {
      id: "Share Amount",
      cell: (info) => info.getValue()?.toGBPString(),
      header: () => <>Share Amount</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: (info) => (
        <>
          {info.row.original.invoiceNumber && (
            <div
              data-test-id={`budget-header-charges-table__download-invoice-button-${info.row.original.chargeFileID}`}
              className="cpl-btn btn-sm cpl-btn-outline"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Download Invoice"
              onClick={(e) => {
                e.stopPropagation();
                downloadInvoice(info.row.original);
              }}
            >
              <FontAwesomeIcon icon={faDownload} size="lg" />
            </div>
          )}
        </>
      ),
      enableHiding: false,
    }),
  ];

  useEffect(() => {
    thunkDispatch(
      getChargesForClientsBudgetHeading({ clientID: clientID!, budgetID: budgetID!, budgetHeaderID: budgetHeaderID! })
    );
  }, [thunkDispatch, clientID, budgetID, budgetHeaderID]);

  const downloadInvoice = async (row: ClientPortalBudgetChargeModel) => {
    const file = await thunkDispatch(downloadChargeFilePDF(row.chargeFileID!)).unwrap();
    downloadFile(file);
  };

  return (
    <>
      <div className="card d-flex">
        <ActivityIndicator isBusy={isLoading}>
          <div className="card-body">
            <CPLTable
              data={charges.charges ?? []}
              columns={columnDefinition}
              sortingState={[{ id: "Budget Header", desc: false }]}
              classNames="cpl-table-nowrap-below-desktop"
            />
          </div>
        </ActivityIndicator>
      </div>
    </>
  );
};

export default ClientTransactionsTable;
